import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import ConfirmDialog from "../../components/ConfirmDialog";

const StockList = () => {
  const [items, setItems] = useState([]);
  const [trays, setTrays] = useState([]);
  const [open, setOpen] = useState(false);
  const [trayOpen, setTrayOpen] = useState(false);

  const [itemName, setItemName] = useState("");
  const [trayName, setTrayName] = useState("");
  const [trayPrice, setTrayPrice] = useState("");
  const [error, setError] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedTrayId, setSelectedTrayId] = useState("");
  const [editItem, setEditItem] = useState(false);
  const [editTray, setEditTray] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDeleteTray, setOpenDeleteTray] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getItemsList();
    getTraysList();
  }, []);

  const handleAddItem = async () => {
    try {
      setSaving(true);
      if (!itemName.trim()) {
        setError("Item name is required");
        setSaving(false)
        return;
      }

      const payload = {
        itemName: itemName
      };

      let path = "/stock/add-item";

      if (editTray) {
        path = "/stock/update/" + selectedItemId;
      }

      const response = await axiosInstance.post(path, payload);

      toast.success(response?.data?.message || "Item added succesfully");

      getItemsList();
      closeAddItemPopup();
      setSaving(false);
    } catch (error) {
      setSaving(false);
      toast.error(
        error.response?.data?.message ||
          "Something went wrong, try again after sometime"
      );
    }
  };

  const handleAddTray = async () => {
    try {
      setSaving(true);
      if (!trayName.trim() || !trayPrice) {
        setError("All fields are required");
        setSaving(false)
        return;
      }

      const payload = {
        name: trayName,
        price: trayPrice
      };

      let path = "/trays";

      if (editTray) {
        path = "/trays/" + selectedTrayId;
      }

      const response = await axiosInstance.post(path, payload);

      toast.success(response?.data?.message || "Tray added succesfully");

      getTraysList();
      closeAddTrayPopup();
      setSaving(false);
    } catch (error) {
      setSaving(false);
      console.log(error)
      toast.error(
        error.response?.data?.message ||
          "Something went wrong, try again after sometime"
      );
    }
  };

  const closeAddItemPopup = () => {
    setOpen(false);
    setError("");
    setItemName("");
    setEditItem(false);
  };

  const closeAddTrayPopup = () => {
    setTrayOpen(false);
    setError("");
    setTrayName("");
    setTrayPrice("");
    setEditTray(false);
  };

  const getItemsList = async () => {
    try {
      const response = await axiosInstance.get("/stock");

      setItems(response?.data?.list);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error retrieving list");
    }
  };

  const getTraysList = async () => {
    try {
      const response = await axiosInstance.get("/trays");

      const list = response?.data?.list.filter(el => el.status !== false)

      setTrays(list);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error retrieving list");
    }
  };

  const openEditItemPopup = (item) => {
    setEditItem(true);
    setSelectedItemId(item?._id);
    setItemName(item?.itemName);
    setOpen(true);
  };

  const openEditTrayPopup = (item) => {
    setEditTray(true);
    setSelectedTrayId(item?._id);
    setTrayName(item?.name);
    setTrayPrice(item?.price);
    setTrayOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      await axiosInstance.delete("stock/" + selectedItemId);
      getItemsList();
      setOpenDelete(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleDeleteTray = async () => {
    try {
      await axiosInstance.post("trays/" + selectedTrayId);
      getTraysList();
      setOpenDeleteTray(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const tableContainerStyle = {
    borderRadius: "16px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    border: "1px solid #eee",
    overflow: "hidden",
  };

  const tableHeaderStyle = {
    background: "linear-gradient(90deg,#f8f9ff,#eef1ff)",
  };

  const rowStyle = {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#fafbff",
      transform: "scale(1.002)",
    },
  };

  return (
    <Box p={3}>
      <Box display="flex" gap={4}>
        {/* ================= ITEMS TABLE ================= */}
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h5" fontWeight="bold">
              Items
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Item
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{
            ...tableContainerStyle,
            maxHeight: 550,
            overflowY: "auto",
          }}>
            <Table stickyHeader>
              <TableHead sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "#f9fafc",
                }}>
                <TableRow sx={rowStyle}>
                  <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                    <b>Item Name</b>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.length === 0 ? (
                  <TableRow sx={rowStyle}>
                    <TableCell colSpan={2} align="center">
                      No items found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item._id} sx={rowStyle}>
                      <TableCell>{item.itemName}</TableCell>

                      <TableCell>
                        <IconButton color="primary" onClick={() => openEditItemPopup(item)}>
                          <FaEdit size={18} />
                        </IconButton>

                        <IconButton
                          color="error"
                          onClick={() =>{ setSelectedItemId(item?._id);setOpenDelete(true)}}
                        >
                          <FaTrash size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ================= TRAYS TABLE ================= */}
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h5" fontWeight="bold">
              Trays
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTrayOpen(true)}
            >
              Add Tray
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{
            ...tableContainerStyle,
            maxHeight: 550,
            overflowY: "auto",
          }}>
            <Table stickyHeader>
              <TableHead sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "#f9fafc",
                }}>
                <TableRow sx={rowStyle}>
                  <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                    <b>Tray Name</b>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                    <b>Price</b>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {trays.length === 0 ? (
                  <TableRow sx={rowStyle}>
                    <TableCell colSpan={2} align="center">
                      No trays found
                    </TableCell>
                  </TableRow>
                ) : (
                  trays.map((item) => (
                    <TableRow key={item._id} sx={rowStyle}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.price}</TableCell>

                      <TableCell>
                        {/* <IconButton color="primary" onClick={() => openEditTrayPopup(item)}>
                          <FaEdit size={18} />
                        </IconButton> */}

                        <IconButton
                          color="error"
                          onClick={() =>{ setSelectedTrayId(item?._id);setOpenDeleteTray(true)}}
                        >
                          <FaTrash size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* ================= ADD ITEM DIALOG ================= */}
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>Add Item</DialogTitle>

        <DialogContent>
          <TextField
            label="Item Name"
            fullWidth
            margin="normal"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => closeAddItemPopup()}>Cancel</Button>

          <Button variant="contained" onClick={handleAddItem}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= ADD SERVICE DIALOG ================= */}
      <Dialog open={trayOpen} fullWidth maxWidth="sm">
        <DialogTitle>Add Item</DialogTitle>

        <DialogContent>
          <TextField
            label="Tray Name"
            fullWidth
            margin="normal"
            value={trayName}
            onChange={(e) => setTrayName(e.target.value)}
          />

          <TextField
            label="Price"
            type="number"
            onWheel={(e) => e.target.blur()}
            value={trayPrice}
            fullWidth
            onChange={(e) => setTrayPrice(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => closeAddTrayPopup()}>Cancel</Button>

          <Button variant="contained" onClick={handleAddTray}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={openDelete || openDeleteTray}
        onClose={()=> openDelete ? setOpenDelete(false) : setOpenDeleteTray(false)}
        onConfirm={() => openDelete ? handleDeleteItem() : handleDeleteTray()}
        message={`Are you sure you want to delete ${openDelete ? 'item' : 'tray'}?`}
      />
    </Box>
  );
};

export default StockList;